import { useId } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import clsx from 'clsx'
import { Monitor, Moon, Sun } from 'lucide-react'

import { useTheme } from '@contexts/ThemeContext'
import { tabIndicatorTransition } from '@utils/animations'

const OPTIONS = [
  { value: 'system', label: 'System', Icon: Monitor },
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon }
]

function ThemeSelector({ className = '', showLabels = false }) {
  const { theme, setTheme } = useTheme()
  const reduceMotion = useReducedMotion()
  const instanceId = useId().replace(/:/g, '')

  return (
    <div
      className={clsx(
        'inline-flex h-12 items-center rounded-lg border border-[var(--x3-border)] bg-[var(--x3-surface)] p-px',
        className
      )}
      role="group"
      aria-label="Color theme"
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const isSelected = theme === value

        return (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            className={clsx(
              'relative inline-flex h-11 items-center justify-center gap-2 rounded px-2 text-xs font-medium transition-colors',
              showLabels ? 'min-w-0 flex-1' : 'w-11',
              isSelected
                ? 'text-white'
                : 'text-[var(--x3-text-muted)] hover:bg-[var(--x3-surface-subtle)] hover:text-[var(--x3-text)]'
            )}
            aria-label={`Use ${label.toLowerCase()} theme`}
            aria-pressed={isSelected}
            title={`${label} theme`}
          >
            {isSelected && (
              <motion.span
                layoutId={`theme-selector-indicator-${instanceId}`}
                className="absolute inset-0 rounded-md bg-[var(--x3-action)] shadow-sm"
                transition={
                  reduceMotion ? { duration: 0 } : tabIndicatorTransition
                }
              />
            )}
            <Icon
              className="relative z-10 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            {showLabels && <span className="relative z-10">{label}</span>}
          </button>
        )
      })}
    </div>
  )
}

export default ThemeSelector
